import { useState } from 'react'
import { Send } from 'lucide-react'
import { ChatProvider } from 'interfaces/chat.interface'
import Select from 'components/select/select.component'

type ChatToolbarProps = {
  onSendMessage: (message: string) => void
  providers: ChatProvider[]
  selectedProvider: string
  onProviderChange: (provider: string) => void
  isLoading?: boolean
  disabled?: boolean
  userRole?: string
}

const ChatToolbar: React.FC<ChatToolbarProps> = ({
  onSendMessage,
  providers,
  selectedProvider,
  onProviderChange,
  isLoading = false,
  disabled = false,
  userRole,
}) => {
  const [message, setMessage] = useState('')

  const isAdmin = userRole === 'super'

  const handleSendMessage = () => {
    if (message.trim() && !isLoading && !disabled) {
      onSendMessage(message.trim())
      setMessage('')
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSendMessage()
    }
  }

  return (
    <div className="w-full">
      {/* Provider Selector - Only show for admin users */}
      {isAdmin && (
        <div className="w-full max-w-[340px] mx-auto mb-2 px-1">
          <label className="text-[10px] font-semibold text-text-muted uppercase tracking-wider mb-1 block">
            AI Model
          </label>
          <Select
            id="provider-select"
            name="provider"
            value={selectedProvider}
            onChange={(value) => onProviderChange(value)}
            disabled={disabled}
            showLabel={false}
            dropUp
            options={providers.map(provider => ({
              value: provider.name,
              label: provider.display_name,
            }))}
            className="w-full"
          />
        </div>
      )}

      <div className="w-full max-w-[340px] mx-auto bg-white rounded-lg border border-border">
        {/* Message Input */}
        <div className="flex flex-col gap-2 p-3 h-[126px]">
          <textarea
            className="w-full flex-1 bg-white text-sm border-none px-1 py-1 focus:outline-none focus:border-none focus:ring-0 active:outline-none placeholder:text-text-muted placeholder:opacity-50 resize-none"
            placeholder="Reply to Career Sherpa..."
            value={message}
            onChange={e => setMessage(e.target.value)}
            onKeyDown={handleKeyPress}
            disabled={disabled || isLoading}
            rows={3}
          />

          <div className="flex items-center justify-end">
            <button
              className={`w-[40px] h-[40px] rounded-full bg-[#2B8565] flex items-center justify-center transition-all duration-200 ${
                message.trim() && !disabled && !isLoading
                  ? 'hover:brightness-90'
                  : 'opacity-50 cursor-not-allowed'
              }`}
              onClick={handleSendMessage}
              disabled={disabled || isLoading || !message.trim()}
            >
              {isLoading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <Send size={16} color="white" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Disclaimer */}
      <div className="text-left mt-2 w-full px-4">
        <p className="text-sm font-normal leading-5 text-text-muted whitespace-nowrap">
          Career Sherpa can make mistakes. Check important info.
        </p>
      </div>
    </div>
  )
}

export default ChatToolbar
